import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { parseContentDispositionFilename } from '../core/api-response-utils';
import { FilterSpec, Pageable, WrapperPageableResponse, WrapperResponse } from './service-common';
import type {
  CreateMitMandateRequest,
  CreateMitTransactionRequest,
  MitConfigDTO,
  MitConsentDetailDTO,
  MitConsentType,
  MitExpiredReason,
  MitLimits,
  MitLimitsRequest,
  MitMandateDTO,
  MitMandateStatusChangeRequest,
  MitMandateStatusValue,
  MitRejectionCode,
  MitTransactionDTO,
  MitTransactionStatusValue,
  MitTransactionType,
  MitUsageDTO,
  PublicMitConsentRequest,
  PublicMitEnrollRequest,
  PublicMitEnrollmentResultDTO,
  PublicMitMandateDTO,
  PublicMitSavedCardDTO,
  ResolveMitTransactionRequest,
  CollectionAttemptEventDTO
} from '../core/models';

export type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

export const MIT_MANDATE_STATUS_LABELS: Record<MitMandateStatusValue, string> = {
  PENDING_ENROLLMENT: 'Pendiente de registro',
  ACTIVE: 'Activo',
  SUSPENDED: 'Suspendido',
  REVOKED: 'Revocado',
  EXPIRED: 'Vencido'
};

export const MIT_EXPIRED_REASON_LABELS: Record<MitExpiredReason, string> = {
  ENROLLMENT_LINK_EXPIRED: 'Venció la liga de registro',
  ENROLLMENT_ATTEMPTS_EXCEEDED: 'Se agotaron los intentos de registro',
  VALIDITY_ENDED: 'Terminó la vigencia'
};

export const MIT_TRANSACTION_STATUS_LABELS: Record<MitTransactionStatusValue, string> = {
  PROCESSING: 'En proceso',
  APPROVED: 'Aprobada',
  DECLINED: 'Declinada',
  REJECTED: 'Rechazada',
  PENDING_RECONCILIATION: 'En revisión',
  VOID_PENDING: 'Cancelación pendiente',
  VOIDED: 'Cancelada',
  VOID_FAILED: 'Cancelación fallida'
};

export const MIT_TRANSACTION_TYPE_LABELS: Record<MitTransactionType, string> = {
  CARD_VERIFICATION: 'Validación de tarjeta',
  INITIAL_CHARGE: 'Primer cobro',
  MIT_CHARGE: 'Cobro MIT'
};

export const MIT_CONSENT_TYPE_LABELS: Record<MitConsentType, string> = {
  ENROLLMENT: 'Registro de tarjeta',
  LIMITS_CHANGE: 'Aumento de límites',
  CARD_REPLACEMENT: 'Reemplazo de tarjeta'
};

export const MIT_ACTOR_LABELS: Record<string, string> = {
  MERCHANT: 'Comercio',
  CUSTOMER: 'Cliente',
  PENTO: 'Pento',
  SYSTEM: 'Sistema',
  OWNER: 'Usuario',
  USER: 'Usuario',
  API: 'Integración API'
};

export const MIT_REJECTION_LABELS: Record<MitRejectionCode, string> = {
  MANDATE_NOT_ACTIVE: 'El mandato no está activo',
  MANDATE_NOT_YET_VALID: 'El mandato aún no inicia su vigencia',
  MANDATE_OUT_OF_VALIDITY: 'El mandato está fuera de su vigencia',
  CARD_EXPIRED: 'La tarjeta está vencida',
  CARD_INACTIVE: 'La tarjeta no está activa',
  CARD_TYPE_NOT_ALLOWED: 'El tipo de tarjeta no está permitido',
  PROVIDER_NOT_SUPPORTED: 'El proveedor de pago no admite cobros bajo demanda',
  CURRENCY_NOT_SUPPORTED: 'La moneda no coincide con la del mandato',
  AMOUNT_BELOW_MIN: 'El monto es menor al mínimo por cobro del mandato',
  MAX_AMOUNT_PER_TRANSACTION: 'Excede el monto máximo por cobro del mandato',
  PREVIOUS_TRANSACTION_UNRESOLVED: 'Hay un cobro anterior en revisión; espera a que se resuelva',
  MIN_INTERVAL: 'No ha pasado el tiempo mínimo entre cobros',
  POSSIBLE_DUPLICATE: 'Parece un cobro duplicado (mismo monto y concepto hace poco)',
  MAX_AMOUNT_PER_DAY: 'Excede el monto máximo diario del mandato',
  MAX_TRANSACTIONS_PER_DAY: 'Excede el número máximo de cobros por día',
  MAX_AMOUNT_PER_MONTH: 'Excede el monto máximo mensual del mandato',
  MAX_TRANSACTIONS_PER_MONTH: 'Excede el número máximo de cobros por mes',
  MAX_TOTAL_AMOUNT: 'Excede el monto máximo acumulado del mandato',
  MAX_TOTAL_TRANSACTIONS: 'Excede el número máximo de cobros del mandato',
  COMPANY_DAILY_CAP: 'Excede el tope diario de cobros bajo demanda de la compañía',
  COMPANY_RATE_LIMIT: 'Demasiados cobros en poco tiempo; intenta en un minuto'
};

export function mitMandateStatusSeverity(status: MitMandateStatusValue | undefined): TagSeverity {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ENROLLMENT') return 'info';
  if (status === 'SUSPENDED') return 'warn';
  if (status === 'REVOKED') return 'danger';
  return 'secondary';
}

export function mitTransactionStatusSeverity(status: MitTransactionStatusValue | undefined): TagSeverity {
  if (status === 'APPROVED' || status === 'VOIDED') return 'success';
  if (status === 'PROCESSING') return 'info';
  if (status === 'PENDING_RECONCILIATION' || status === 'VOID_PENDING') return 'warn';
  if (status === 'DECLINED' || status === 'REJECTED' || status === 'VOID_FAILED') return 'danger';
  return 'secondary';
}

/** El cargo de registro se aprobó: primer cobro aprobado o validación pendiente de cancelar o cancelada. */
export function mitCardRegistered(status: MitTransactionStatusValue | undefined): boolean {
  return status === 'APPROVED' || status === 'VOID_PENDING' || status === 'VOIDED';
}

/** Motivo legible del estado actual (vencimiento, suspensión o revocación), o `null` si no aplica. */
export function mitMandateStatusReason(mandate: MitMandateDTO | null | undefined): string | null {
  if (!mandate) return null;
  if (mandate.status === 'EXPIRED' && mandate.expiredReason) return MIT_EXPIRED_REASON_LABELS[mandate.expiredReason];
  if (mandate.status === 'SUSPENDED') {
    const by = mandate.suspendedBy ? MIT_ACTOR_LABELS[mandate.suspendedBy] : null;
    return [by ? `Por ${by.toLowerCase()}` : null, mandate.suspendedReason].filter(Boolean).join(': ') || null;
  }
  if (mandate.status === 'REVOKED') {
    const by = mandate.revokedBy ? MIT_ACTOR_LABELS[mandate.revokedBy] : null;
    return [by ? `Por ${by.toLowerCase()}` : null, mandate.revokedReason].filter(Boolean).join(': ') || null;
  }
  return null;
}

export function mitRejectionLabel(code: string | undefined | null): string {
  if (!code) return '';
  return MIT_REJECTION_LABELS[code as MitRejectionCode] ?? code;
}

const MONEY = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Resumen en lenguaje claro: «Hasta $2,500.00 por cobro y $10,000.00 por mes». */
export function mitLimitsSummary(limits: MitLimits | null | undefined): string {
  if (!limits) return '—';
  const parts: string[] = [];
  if (limits.maxAmountPerTransaction != null) parts.push(`${MONEY.format(limits.maxAmountPerTransaction)} por cobro`);
  if (limits.maxAmountPerMonth != null) parts.push(`${MONEY.format(limits.maxAmountPerMonth)} por mes`);
  return parts.length ? `Hasta ${parts.join(' y ')}` : '—';
}

export interface MitLimitRow {
  key: keyof MitLimits;
  label: string;
  kind: 'amount' | 'count' | 'seconds';
}

/** Orden y etiquetas de los límites para tablas y comparativos. */
export const MIT_LIMIT_ROWS: MitLimitRow[] = [
  { key: 'maxAmountPerTransaction', label: 'Máximo por cobro', kind: 'amount' },
  { key: 'minAmountPerTransaction', label: 'Mínimo por cobro', kind: 'amount' },
  { key: 'maxAmountPerDay', label: 'Máximo por día', kind: 'amount' },
  { key: 'maxAmountPerMonth', label: 'Máximo por mes', kind: 'amount' },
  { key: 'maxTotalAmount', label: 'Máximo acumulado', kind: 'amount' },
  { key: 'maxTransactionsPerDay', label: 'Cobros por día', kind: 'count' },
  { key: 'maxTransactionsPerMonth', label: 'Cobros por mes', kind: 'count' },
  { key: 'maxTotalTransactions', label: 'Cobros en total', kind: 'count' },
  { key: 'minIntervalSeconds', label: 'Tiempo mínimo entre cobros', kind: 'seconds' },
  { key: 'maxConsecutiveDeclines', label: 'Declinaciones seguidas permitidas', kind: 'count' }
];

export function mitLimitValue(row: MitLimitRow, limits: MitLimits | null | undefined): string {
  const value = limits?.[row.key];
  if (value == null) return 'Sin límite';
  if (row.kind === 'amount') return MONEY.format(value);
  if (row.kind === 'seconds') {
    const minutes = Math.round(value / 60);
    return minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
  }
  return String(value);
}

const LIVE_STATUSES: (MitMandateStatusValue | undefined)[] = ['PENDING_ENROLLMENT', 'ACTIVE', 'SUSPENDED'];

/** Acciones por estado; la API valida lo mismo y decide. `isPlatform` = tiene `MIT_CONFIG_WRITE`. */
/** Misma regla que el backend para que la liga de registro o de reemplazo de tarjeta siga vigente. */
export function mitEnrollmentOpen(mandate: MitMandateDTO | null | undefined): boolean {
  if (!mandate?.enrollmentPurpose || !mandate.enrollmentExpiresAt) return false;
  if (new Date(mandate.enrollmentExpiresAt).getTime() <= Date.now()) return false;
  return mandate.status === 'PENDING_ENROLLMENT'
    ? mandate.enrollmentPurpose === 'ENROLLMENT'
    : (mandate.status === 'ACTIVE' || mandate.status === 'SUSPENDED') && mandate.enrollmentPurpose === 'CARD_REPLACEMENT';
}

export function mitMandateActions(mandate: MitMandateDTO | null | undefined, isPlatform = false) {
  const status = mandate?.status;
  const live = LIVE_STATUSES.includes(status);
  return {
    charge: status === 'ACTIVE',
    changeLimits: live,
    enrollmentLink: status === 'PENDING_ENROLLMENT'
      || (status === 'EXPIRED' && mandate?.expiredReason !== 'VALIDITY_ENDED'),
    resendEnrollmentLink: mitEnrollmentOpen(mandate),
    resendManageLink: status === 'ACTIVE' || status === 'SUSPENDED',
    suspend: status === 'ACTIVE',
    reactivate: status === 'SUSPENDED' && (mandate?.suspendedBy !== 'PENTO' || isPlatform),
    revoke: live
  };
}

/** Error de la API: mensaje y `metadata.errorCode` cuando existe. */
export function mitApiError(err: unknown, fallback: string): { message: string; code?: string } {
  const body = (err as { error?: { message?: string; metadata?: { errorCode?: string } } } | null)?.error;
  return { message: body?.message || fallback, code: body?.metadata?.errorCode };
}

@Injectable({ providedIn: 'root' })
export class MitService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // ------------------------------------------------------------ mandatos

  search(spec: FilterSpec = {}, pageable: Pageable = { page: 0, size: 20 }): Observable<WrapperPageableResponse<MitMandateDTO>> {
    return this.http.get<WrapperPageableResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates`, {
      params: this.params(spec, pageable)
    });
  }

  list(spec: FilterSpec = {}, pageable: Pageable = { page: 0, size: 20 }): Observable<WrapperPageableResponse<MitMandateDTO>> {
    return this.http.get<WrapperPageableResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/list`, {
      params: this.params(spec, pageable)
    });
  }

  exportCsv(spec: FilterSpec = {}, sort: string[] = ['createdAt,desc']): Observable<{ blob: Blob; filename: string }> {
    let params = this.specParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }
    return this.http
      .get(`${this.apiUrl}/mit-mandates/export`, { params, responseType: 'blob', observe: 'response' })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename: parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'mandatos.csv'
        }))
      );
  }

  getById(id: number): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.get<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}`);
  }

  consent(id: number, consentId: number): Observable<WrapperResponse<MitConsentDetailDTO>> {
    return this.http.get<WrapperResponse<MitConsentDetailDTO>>(`${this.apiUrl}/mit-mandates/${id}/consents/${consentId}`);
  }

  usage(id: number): Observable<WrapperResponse<MitUsageDTO>> {
    return this.http.get<WrapperResponse<MitUsageDTO>>(`${this.apiUrl}/mit-mandates/${id}/usage`);
  }

  create(request: CreateMitMandateRequest): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates`, request);
  }

  /** `metadata.pendingConsent = true` cuando el aumento espera la aceptación del cliente. */
  updateLimits(id: number, request: MitLimitsRequest): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.put<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/limits`, request);
  }

  /** Reenvía al correo guardado en el mandato una liga nueva con el mismo vencimiento; la anterior deja de funcionar. */
  resendEnrollmentLink(id: number): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/enrollment-link/resend`, {});
  }

  /** Reenvía la liga de gestión al correo del mandato; desde ahí el cliente consulta cargos, cambia su tarjeta o revoca. */
  resendManageLink(id: number): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/manage-link/resend`, {});
  }

  regenerateEnrollmentLink(id: number): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/enrollment-link`, {});
  }

  suspend(id: number, request: MitMandateStatusChangeRequest = {}): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/suspend`, request);
  }

  reactivate(id: number): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/reactivate`, {});
  }

  revoke(id: number, request: MitMandateStatusChangeRequest = {}): Observable<WrapperResponse<MitMandateDTO>> {
    return this.http.post<WrapperResponse<MitMandateDTO>>(`${this.apiUrl}/mit-mandates/${id}/revoke`, request);
  }

  /** Respuesta completa: 201 nuevo, 200 con `Idempotent-Replayed`, 409 con `Retry-After`. */
  createTransaction(
    mandateId: number,
    request: CreateMitTransactionRequest,
    idempotencyKey: string
  ): Observable<HttpResponse<WrapperResponse<MitTransactionDTO>>> {
    return this.http.post<WrapperResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-mandates/${mandateId}/transactions`, request, {
      headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }),
      observe: 'response'
    });
  }

  mandateTransactions(
    mandateId: number,
    spec: FilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponse<MitTransactionDTO>> {
    return this.http.get<WrapperPageableResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-mandates/${mandateId}/transactions`, {
      params: this.params(spec, pageable)
    });
  }

  // ------------------------------------------------------------ transacciones

  searchTransactions(spec: FilterSpec = {}, pageable: Pageable = { page: 0, size: 20 }): Observable<WrapperPageableResponse<MitTransactionDTO>> {
    return this.http.get<WrapperPageableResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-transactions`, {
      params: this.params(spec, pageable)
    });
  }

  exportTransactionsCsv(spec: FilterSpec = {}, sort: string[] = ['createdAt,desc']): Observable<{ blob: Blob; filename: string }> {
    let params = this.specParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }
    return this.http
      .get(`${this.apiUrl}/mit-transactions/export`, { params, responseType: 'blob', observe: 'response' })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename: parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'transacciones-bajo-demanda.csv'
        }))
      );
  }

  getTransaction(id: number): Observable<WrapperResponse<MitTransactionDTO>> {
    return this.http.get<WrapperResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-transactions/${id}`);
  }

  /** Historial de la transacción en orden cronológico. */
  getTransactionEvents(id: number): Observable<WrapperResponse<CollectionAttemptEventDTO[]>> {
    return this.http.get<WrapperResponse<CollectionAttemptEventDTO[]>>(`${this.apiUrl}/mit-transactions/${id}/events`);
  }

  resolveTransaction(id: number, request: ResolveMitTransactionRequest): Observable<WrapperResponse<MitTransactionDTO>> {
    return this.http.post<WrapperResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-transactions/${id}/resolve`, request);
  }

  /** Plataforma: reintenta de inmediato la cancelación de un cargo de validación pendiente o fallido. */
  voidTransaction(id: number): Observable<WrapperResponse<MitTransactionDTO>> {
    return this.http.post<WrapperResponse<MitTransactionDTO>>(`${this.apiUrl}/mit-transactions/${id}/void`, {});
  }

  // ------------------------------------------------------------ configuración

  getConfig(companyId: number): Observable<WrapperResponse<MitConfigDTO>> {
    return this.http.get<WrapperResponse<MitConfigDTO>>(`${this.apiUrl}/companies/${companyId}/mit-config`);
  }

  // ------------------------------------------------------------ públicos (hash)

  getPublic(id: number, hash: string): Observable<WrapperResponse<PublicMitMandateDTO>> {
    return this.http.get<WrapperResponse<PublicMitMandateDTO>>(`${this.apiUrl}/public/mit-mandates/${id}`, {
      params: new HttpParams().set('hash', hash)
    });
  }

  enrollPublic(id: number, hash: string, request: PublicMitEnrollRequest): Observable<WrapperResponse<PublicMitEnrollmentResultDTO>> {
    return this.http.post<WrapperResponse<PublicMitEnrollmentResultDTO>>(`${this.apiUrl}/public/mit-mandates/${id}/enroll`, request, {
      params: new HttpParams().set('hash', hash)
    });
  }

  consentPublic(id: number, hash: string, request: PublicMitConsentRequest): Observable<WrapperResponse<PublicMitMandateDTO>> {
    return this.http.post<WrapperResponse<PublicMitMandateDTO>>(`${this.apiUrl}/public/mit-mandates/${id}/consent`, request, {
      params: new HttpParams().set('hash', hash)
    });
  }

  getPublicManage(id: number, hash: string): Observable<WrapperResponse<PublicMitMandateDTO>> {
    return this.http.get<WrapperResponse<PublicMitMandateDTO>>(`${this.apiUrl}/public/mit-mandates/${id}/manage`, {
      params: new HttpParams().set('hash', hash)
    });
  }

  /** Tarjetas guardadas del cliente; con la liga de gestión o con la de registro mientras siga abierta. */
  savedCardsPublic(id: number, hash: string): Observable<WrapperResponse<PublicMitSavedCardDTO[]>> {
    return this.http.get<WrapperResponse<PublicMitSavedCardDTO[]>>(`${this.apiUrl}/public/mit-mandates/${id}/cards`, {
      params: new HttpParams().set('hash', hash)
    });
  }

  /** Devuelve la lista actualizada. */
  deleteSavedCardPublic(id: number, hash: string, cardId: number): Observable<WrapperResponse<PublicMitSavedCardDTO[]>> {
    return this.http.delete<WrapperResponse<PublicMitSavedCardDTO[]>>(`${this.apiUrl}/public/mit-mandates/${id}/cards/${cardId}`, {
      params: new HttpParams().set('hash', hash)
    });
  }

  /** Solo con la liga de gestión; devuelve la liga para firmar con la tarjeta nueva. */
  requestCardReplacementPublic(id: number, hash: string): Observable<WrapperResponse<string>> {
    return this.http.post<WrapperResponse<string>>(`${this.apiUrl}/public/mit-mandates/${id}/card-replacement`, {}, {
      params: new HttpParams().set('hash', hash)
    });
  }

  revokePublic(id: number, hash: string): Observable<WrapperResponse<PublicMitMandateDTO>> {
    return this.http.post<WrapperResponse<PublicMitMandateDTO>>(`${this.apiUrl}/public/mit-mandates/${id}/revoke`, {}, {
      params: new HttpParams().set('hash', hash)
    });
  }

  private specParams(spec: FilterSpec): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, Array.isArray(value) ? value.join(',') : String(value));
      }
    });
    return params;
  }

  private params(spec: FilterSpec, pageable: Pageable): HttpParams {
    let params = this.specParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    pageable.sort?.forEach((sort) => {
      params = params.append('sort', sort);
    });
    return params;
  }
}
